import React, { useState, useEffect } from 'react';
import { ArrowUp, ArrowRight, ArrowDown } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TRAITS = [
  { key: 'openness', label: 'Curiosity', color: '#7B4FD8', description: 'How open they are to new experiences and ideas.' },
  { key: 'conscientiousness', label: 'Follow-through', color: '#2563EB', description: 'How consistently they complete what they start.' },
  { key: 'extraversion', label: 'Social Energy', color: '#F5831F', description: 'How energised they feel in social situations.' },
  { key: 'agreeableness', label: 'Empathy', color: '#2EAA5C', description: 'How naturally they tune in to how others are feeling.' },
  { key: 'neuroticism', label: 'Emotional Sensitivity', color: '#EC4899', description: 'How strongly they feel and respond to emotions.' },
];

const GrowthArrow = ({ direction }) => {
  if (direction === 'up') return <ArrowUp size={12} style={{ color: '#2EAA5C' }} />;
  if (direction === 'down') return <ArrowDown size={12} style={{ color: '#F59E0B' }} />;
  return <ArrowRight size={12} style={{ color: '#9CA3AF' }} />;
};

/**
 * TeenOceanProfile — Shows on the Parent Dashboard.
 * Displays the five plain-English bars with growth indicators.
 * No reflection paragraph, no numerical scores, no OCEAN terminology.
 */
const TeenOceanProfile = ({ teenUserId }) => {
  const [profile, setProfile] = useState(null);

  useEffect(() => {
    const load = async () => {
      try {
        const token = localStorage.getItem('hibff_auth_token') || sessionStorage.getItem('hibff_auth_token');
        const res = await fetch(`${API_URL}/api/ocean/teen-profile/${teenUserId}`, {
          headers: { 'Authorization': `Bearer ${token}` },
        });
        if (res.ok) {
          const data = await res.json();
          setProfile(data);
        }
      } catch { /* silent */ }
    };
    if (teenUserId) load();
  }, [teenUserId]);

  if (!profile?.has_profile) return null;

  const scores = profile.scores || {};
  const growth = profile.growth || {};
  const showGrowth = profile.history_count > 0;

  return (
    <div className="card" data-testid="teen-ocean-profile">
      <div className="card-header">
        <h3 className="card-title text-base">
          {profile.teen_name ? `${profile.teen_name}'s Profile` : "Your teenager's profile"}
        </h3>
      </div>
      <p className="text-xs px-4 -mt-2 mb-3" style={{ color: '#888' }}>
        This profile is based on {profile.teen_name || 'your teenager'}&apos;s own responses. It updates every 90 days as they grow.
      </p>
      <div className="px-4 pb-4">
        {TRAITS.map((trait) => {
          const score = scores[trait.key] || 3;
          const pct = Math.max(0, Math.min(100, (score / 5) * 100));
          return (
            <div key={trait.key} className="mb-4" data-testid={`parent-trait-${trait.key}`}>
              <div className="flex items-center justify-between mb-1">
                <div className="flex items-center gap-1.5">
                  <span className="text-xs font-semibold" style={{ color: '#1A1209' }}>{trait.label}</span>
                  {showGrowth && growth[trait.key] && <GrowthArrow direction={growth[trait.key]} />}
                </div>
              </div>
              <div className="w-full h-2 rounded-full" style={{ backgroundColor: '#F0F0F0' }}>
                <div className="h-full rounded-full transition-all duration-700" style={{ width: `${pct}%`, backgroundColor: trait.color }} />
              </div>
              <p className="text-[10px] mt-0.5" style={{ color: '#AAA' }}>{trait.description}</p>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default TeenOceanProfile;
